import { useNavigate } from 'react-router-dom';

/**
 * API Integrations Hub
 *
 * Landing page for configuring external data sources that feed KPIs.
 * Each integration is a card with a status badge and a Configure button.
 */

const INTEGRATIONS = [
  {
    id: 'spotio',
    name: 'SPOTIO',
    description: 'Pull visit counts bucketed by pipeline stage (cold vs customer) into leaderboard KPIs.',
    icon: '📍',
    color: '#6d28d9',
    route: '/admin-mod/spotio',
    status: 'available',
    permission: 'system_settings',
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Pull KPI values directly from a published Google Sheet on a schedule. Useful for manual trackers (lost accounts, sign requests, etc.).',
    icon: '📊',
    color: '#16a34a',
    route: '/admin-mod/google-sheets',
    status: 'available',
    permission: 'system_settings',
  },
];

export default function IntegrationsHubPage() {
  const navigate = useNavigate();

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>API Integrations</h1>
        <button className="btn btn-outline" onClick={() => navigate('/admin-mod/leaderboard-kpis')}>
          ← Back to KPI Setup
        </button>
      </div>

      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>
        Connect external data sources that automatically feed your leaderboard KPIs. Each integration runs on a schedule and updates KPI values without manual entry.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {INTEGRATIONS.map(int => (
          <div key={int.id} style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid #e2e8f0',
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            transition: 'all 0.15s',
            opacity: int.status === 'coming-soon' ? 0.65 : 1,
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: int.color + '15',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26,
              }}>
                {int.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#0f172a' }}>{int.name}</h2>
                  {int.status === 'coming-soon' && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6,
                      background: '#fef3c7', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                      Coming Soon
                    </span>
                  )}
                  {int.status === 'available' && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6,
                      background: '#dcfce7', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                      Available
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5, minHeight: 54 }}>
              {int.description}
            </p>

            {/* Action */}
            <div style={{ marginTop: 'auto', paddingTop: 8 }}>
              {int.status === 'available' ? (
                <button
                  className="btn btn-gold"
                  onClick={() => navigate(int.route)}
                  style={{ width: '100%' }}
                >
                  Configure {int.name}
                </button>
              ) : (
                <button
                  className="btn btn-outline"
                  disabled
                  style={{ width: '100%', cursor: 'not-allowed' }}
                >
                  Not Yet Available
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Future integrations hint */}
      <div style={{
        marginTop: 32,
        padding: 20,
        background: '#f8fafc',
        borderRadius: 12,
        border: '1px dashed #cbd5e1',
        textAlign: 'center',
        color: '#64748b',
        fontSize: 12,
      }}>
        <b style={{ color: '#0f172a' }}>Need another integration?</b> More data sources can be added here as they're built (Salesforce, HubSpot, Stripe, etc.). Each integration maps incoming data to a KPI and runs automatically on a schedule.
      </div>
    </div>
  );
}
